import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { memo } from 'react'
import { twMerge } from 'tailwind-merge'
import { Th } from '../Th'
import { Td } from '../Td'

const SimpleTableComponent = <T,>({
  className,
  columns,
  data,
  defaultSortColumn,
}: {
  className?: string
  columns: ColumnDef<T, unknown>[]
  data: T[]
  defaultSortColumn?: string
}) => {
  const initialState = defaultSortColumn
    ? { sorting: [{ id: defaultSortColumn, desc: false }] }
    : {}

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
  })

  return (
    <div
      className={twMerge(
        'rounded-lg overflow-x-auto border border-neutral-300 w-full',
        className,
      )}
    >
      <table className="text-sm w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  className="border-none"
                  key={header.id}
                  label={
                    header.isPlaceholder
                      ? ''
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                  }
                  clickable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  sortDir={
                    header.column.getIsSorted() === 'asc'
                      ? 'asc'
                      : header.column.getIsSorted() === 'desc'
                      ? 'desc'
                      : false
                  }
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="group">
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  className={twMerge(
                    'px-4 py-2 border-x-0 group-last:border-b-0',
                    className,
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const SimpleTable = memo(
  SimpleTableComponent,
) as typeof SimpleTableComponent
